import { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Zap, 
  Car as CarIcon, 
  Info, 
  ChevronDown, 
  ChevronUp, 
  TrendingUp, 
  TrendingDown,
  ArrowLeft,
  Settings,
  ShieldCheck,
  Wrench,
  AlertCircle
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { useTranslations } from '../i18n';
import { usePreferences } from '../contexts/PreferencesContext';
import { calculateRepairAnalysis } from '../lib/repairAnalysis';
import { formatCurrency } from '../lib/conversions';
import { supabase } from '../lib/supabase';

type CarSwitchingSimulatorProps = {
  car: any;
  logEntries: any[];
  isReadOnly?: boolean;
  onBackToHealth: () => void;
};

export function CarSwitchingSimulator({ car, logEntries, isReadOnly, onBackToHealth }: CarSwitchingSimulatorProps) {
  const { t } = useTranslations();
  const { preferences: userPreferences } = usePreferences();
  
  // Load initial simulation state from DB or defaults
  const initialSim = car.switch_simulation_json || {};
  
  const [purchaseMode, setPurchaseMode] = useState<'cash' | 'lease'>(initialSim.purchaseMode || 'cash');
  const [condition, setCondition] = useState<'new' | 'used'>(initialSim.condition || 'used');
  const [purchasePrice, setPurchasePrice] = useState<number | ''>(initialSim.purchasePrice ?? (car.ai_baseline_json?.estimated_new_price || ''));
  const [downPayment, setDownPayment] = useState<number | ''>(initialSim.downPayment ?? 0);
  const [monthlyPayment, setMonthlyPayment] = useState<number | ''>(initialSim.monthlyPayment ?? '');
  const [insuranceYr, setInsuranceYr] = useState<number | ''>(initialSim.insuranceYr ?? 600);
  const [maintenanceYr, setMaintenanceYr] = useState<number | ''>(initialSim.maintenanceYr ?? 800);
  const [tyreCost, setTyreCost] = useState<number | ''>(initialSim.tyreCost ?? 400);
  const [depreciationPct, setDepreciationPct] = useState<number>(initialSim.depreciationPct ?? 50);
  const [ownershipYears, setOwnershipYears] = useState<number>(initialSim.ownershipYears ?? 5);
  const [startupCosts, setStartupCosts] = useState(initialSim.startupCosts || {
    inspection: true,
    maintenance: true,
    registration: true
  });
  
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const saveTimeoutRef = useRef<any>(null);

  // Auto-save logic
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    
    saveTimeoutRef.current = setTimeout(async () => {
      if (isReadOnly) return;
      const simData = {
        purchaseMode,
        condition,
        purchasePrice,
        downPayment,
        monthlyPayment,
        insuranceYr,
        maintenanceYr,
        tyreCost,
        depreciationPct,
        ownershipYears,
        startupCosts
      };
      
      setIsSaving(true);
      try {
        await supabase
          .from('cars')
          .update({ switch_simulation_json: simData })
          .eq('id', car.id);
        car.switch_simulation_json = simData; // Update local ref
      } catch (e) {
        console.error('Error saving simulation:', e);
      } finally {
        setIsSaving(false);
      }
    }, 1000); // 1s debounce

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [purchaseMode, condition, purchasePrice, downPayment, monthlyPayment, insuranceYr, maintenanceYr, tyreCost, depreciationPct, ownershipYears, startupCosts, car]);

  const analysis = useMemo(() => {
    return calculateRepairAnalysis(car, logEntries, {
      newCarPrice: Number(purchasePrice) || 0,
      newCarIsUsed: condition === 'used',
      newCarMaintenance: Number(maintenanceYr) || 0,
      newCarInsurance: Number(insuranceYr) || 0,
      newCarOwnershipYears: ownershipYears,
      newCarResaleValuePct: 100 - depreciationPct,
      newCarDownPayment: Number(downPayment) || 0,
      newCarLeaseMonths: ownershipYears * 12,
      newCarMonthlyPayment: Number(monthlyPayment) || 0,
      newCarPurchaseMode: purchaseMode,
      newCarStartupCosts: startupCosts,
      newCarTyres: Number(tyreCost) || 0
    });
  }, [car, logEntries, purchasePrice, condition, maintenanceYr, insuranceYr, ownershipYears, depreciationPct, downPayment, monthlyPayment, purchaseMode, startupCosts, tyreCost]);

  const fmt = (val: number) => formatCurrency(val, (car.currency || 'EUR') as any, car.currency_symbol || '€');

  const comp = analysis.comparison;
  const currentMonthly = analysis.monthly_cost_current;
  const diffMonthly = (comp?.monthly_total || 0) - currentMonthly;
  const isCheaper = diffMonthly < 0;

  if (!comp) return null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto px-4 sm:px-4 pb-32 animate-in slide-in-from-right duration-500">
      
      {/* SIM STATUS INDICATOR */}
      <div className="flex justify-end">
         <div className="flex items-center gap-2">
            {isSaving && <div className="animate-pulse w-2 h-2 rounded-full bg-indigo-500" />}
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
              {isSaving ? 'Saving...' : 'Current Simulation'}
            </p>
         </div>
      </div>

      {/* Intro Disclaimer */}
      <div className="p-4 bg-gray-50/50 dark:bg-white/5 rounded-3xl border border-gray-100 dark:border-white/5 border-dashed flex items-start gap-3">
         <AlertCircle className="text-gray-400 mt-0.5 flex-shrink-0" size={18} />
         <p className="text-[10px] font-bold text-gray-500 leading-relaxed italic">
            This is not a recommendation tool. It is a **financial reality simulator** to help you understand the true cost of switching vehicles.
         </p>
      </div>

      {/* Input Section */}
      <div className={`bg-white dark:bg-slate-800 rounded-[2.5rem] border border-gray-100 dark:border-white/10 shadow-sm p-6 space-y-8 ${isReadOnly ? 'pointer-events-none' : ''}`}>
        
        {/* Purchase Mode & Condition Switches */}
        <div className="grid grid-cols-2 gap-4">
           <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 px-1">Buying Mode</label>
              <div className="flex bg-gray-50 dark:bg-slate-900 rounded-2xl p-1 border border-gray-100 dark:border-white/5">
                 <button onClick={() => setPurchaseMode('cash')} className={`flex-1 py-2 text-[9px] font-black uppercase rounded-xl transition-all ${purchaseMode === 'cash' ? 'bg-white dark:bg-slate-700 text-indigo-500 shadow-sm' : 'text-gray-400'}`}>Cash</button>
                 <button onClick={() => setPurchaseMode('lease')} className={`flex-1 py-2 text-[9px] font-black uppercase rounded-xl transition-all ${purchaseMode === 'lease' ? 'bg-white dark:bg-slate-700 text-indigo-500 shadow-sm' : 'text-gray-400'}`}>Lease</button>
              </div>
           </div>
           <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 px-1">Condition</label>
              <div className="flex bg-gray-50 dark:bg-slate-900 rounded-2xl p-1 border border-gray-100 dark:border-white/5">
                 <button onClick={() => setCondition('used')} className={`flex-1 py-2 text-[9px] font-black uppercase rounded-xl transition-all ${condition === 'used' ? 'bg-white dark:bg-slate-700 text-indigo-500 shadow-sm' : 'text-gray-400'}`}>Used</button>
                 <button onClick={() => setCondition('new')} className={`flex-1 py-2 text-[9px] font-black uppercase rounded-xl transition-all ${condition === 'new' ? 'bg-white dark:bg-slate-700 text-indigo-500 shadow-sm' : 'text-gray-400'}`}>New</button>
              </div>
           </div>
        </div>

        {/* Pricing Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
           <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1 mb-1 block">Purchase Price</label>
                <div className="relative">
                   <input type="number" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value === '' ? '' : Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-2xl px-4 py-3 pl-8 text-sm font-black focus:ring-2 focus:ring-indigo-500" placeholder="0" />
                   <span className="absolute left-3 top-3.5 text-gray-400 font-bold">€</span>
                </div>
              </div>

              {purchaseMode === 'lease' && (
                 <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1 mb-1 block">Down Payment</label>
                      <input type="number" value={downPayment} onChange={e => setDownPayment(e.target.value === '' ? '' : Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-2xl px-4 py-2 text-sm font-black" />
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1 mb-1 block">Monthly Lease</label>
                      <input type="number" value={monthlyPayment} onChange={e => setMonthlyPayment(e.target.value === '' ? '' : Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-2xl px-4 py-2 text-sm font-black" />
                    </div>
                 </div>
              )}
           </div>

           <div className="space-y-4">
              {condition === 'used' && (
                 <div className="bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl p-4 border border-amber-100 dark:border-amber-900/30 space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 flex items-center gap-1.5 mb-2">Startup Costs</p>
                    {['inspection', 'maintenance', 'registration'].map(k => (
                       <label key={k} className="flex items-center justify-between cursor-pointer">
                          <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 capitalize">{k}</span>
                          <input type="checkbox" checked={(startupCosts as any)[k]} onChange={e => setStartupCosts({...startupCosts, [k]: e.target.checked})} className="w-4 h-4 rounded border-gray-300 text-amber-500" />
                       </label>
                    ))}
                 </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                 <div>
                   <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1 mb-1 block">Ownership (Y)</label>
                   <input type="number" value={ownershipYears} onChange={e => setOwnershipYears(Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-2xl px-4 py-2 text-sm font-black" />
                 </div>
                 <div>
                   <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1 mb-1 block">Depreciation (%)</label>
                   <input type="number" value={depreciationPct} onChange={e => setDepreciationPct(Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-2xl px-4 py-2 text-sm font-black" />
                 </div>
              </div>
           </div>
        </div>

        {/* Detailed Running Costs Toggle */}
        <div className="pt-4 border-t border-gray-100 dark:border-white/10">
           <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-indigo-500">
              <Settings size={14} /> Running Costs & Options {showAdvanced ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
           </button>
           
           {showAdvanced && (
              <div className="grid grid-cols-3 gap-3 mt-4 animate-in slide-in-from-top-2 duration-300">
                 <div>
                    <label className="text-[9px] font-black text-gray-400 mb-1 block">Insurance (Y)</label>
                    <input type="number" value={insuranceYr} onChange={e => setInsuranceYr(Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-xl px-3 py-2 text-xs font-black" />
                 </div>
                 <div>
                    <label className="text-[9px] font-black text-gray-400 mb-1 block">Maintenance (Y)</label>
                    <input type="number" value={maintenanceYr} onChange={e => setMaintenanceYr(Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-xl px-3 py-2 text-xs font-black" />
                 </div>
                 <div>
                    <label className="text-[9px] font-black text-gray-400 mb-1 block">Tyre Cycle</label>
                    <input type="number" value={tyreCost} onChange={e => setTyreCost(Number(e.target.value))} className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-xl px-3 py-2 text-xs font-black" />
                 </div>
              </div>
           )}
        </div>
      </div>

      {/* Output Section: The Wow Moment */}
      <div className="pt-8 space-y-8">
         <div className={`p-8 rounded-[2.5rem] border-2 shadow-2xl text-center transition-all ${isCheaper ? 'bg-green-500/10 border-green-500/20' : 'bg-indigo-500/10 border-indigo-500/20'}`}>
            <p className="text-sm font-black uppercase tracking-[0.2em] text-gray-400 mb-4">The Verdict</p>
            <h3 className={`text-3xl font-black mb-2 ${isCheaper ? 'text-green-600' : 'text-indigo-600'}`}>
               {isCheaper ? 'Switching actually saves money!' : 'Switching will cost more.'}
            </h3>
            <p className="text-lg font-bold text-gray-700 dark:text-gray-300">
               Delta: <span className="font-black">{fmt(Math.abs(diffMonthly))}</span> / month
            </p>
         </div>

         {/* Side-by-Side Reality Check */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Car */}
            <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] p-8 border border-gray-100 dark:border-white/5 shadow-sm space-y-6">
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-400">
                     <CarIcon size={20} />
                  </div>
                  <h4 className="text-sm font-black uppercase tracking-widest">Current Car</h4>
               </div>
               <div className="space-y-1">
                  <p className="text-xs font-bold text-gray-400">Real Monthly Cost</p>
                  <p className="text-3xl font-black">{fmt(currentMonthly)}</p>
               </div>
               <div className="space-y-4 pt-4 border-t border-gray-50 dark:border-white/5">
                  <div className="flex justify-between text-xs font-bold">
                     <span className="text-gray-400">Maintenance (avg)</span>
                     <span>{fmt(analysis.repairExpenses12m / 12)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-bold">
                     <span className="text-gray-400">Upfront Investment</span>
                     <span className="text-green-500">0 €</span>
                  </div>
               </div>
            </div>

            {/* Potential New Car */}
            <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] p-8 border-2 border-indigo-500/30 shadow-xl space-y-6 relative overflow-hidden">
               <div className="absolute top-0 right-0 px-6 py-2 bg-indigo-500 text-white text-[10px] font-black uppercase rounded-bl-3xl">Reality</div>
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500">
                     <Zap size={20} className="fill-current" />
                  </div>
                  <h4 className="text-sm font-black uppercase tracking-widest">Switching To</h4>
               </div>
               <div className="space-y-1">
                  <p className="text-xs font-bold text-gray-400">Estimated Monthly Cost</p>
                  <p className="text-3xl font-black text-indigo-500">{fmt(comp.monthly_total)}</p>
               </div>
               <div className="space-y-4 pt-4 border-t border-gray-50 dark:border-white/5">
                  <div className="flex justify-between text-xs font-bold">
                     <span className="text-gray-400">Running Cost Total</span>
                     <span>{fmt(comp.monthly_maintenance + comp.monthly_insurance + (Number(comp.monthly_tyres) || 0))}</span>
                  </div>
                  <div className="flex justify-between text-xs font-bold">
                     <span className="text-gray-400">Upfront Investment *</span>
                     <span className="text-indigo-600 font-black">{fmt(comp.upfront_cost)}</span>
                  </div>
               </div>
            </div>
         </div>

         {/* Upfront Investment Breakdown */}
         <div className="bg-slate-950 text-white rounded-[2.5rem] p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
               <h4 className="text-sm font-black uppercase tracking-widest text-slate-500">Upfront Investment Breakdown</h4>
               <p className="text-2xl font-black">{fmt(comp.upfront_cost)}</p>
            </div>
            <div className="space-y-4 max-w-sm">
               <div className="flex justify-between text-sm">
                  <span className="text-slate-400 font-bold">New Car Price</span>
                  <span className="font-black">{fmt(Number(purchasePrice) || 0)}</span>
               </div>
               <div className="flex justify-between text-sm">
                  <span className="text-slate-400 font-bold">Trade-in Current Car</span>
                  <span className="font-black text-green-400">-{fmt(analysis.currentMarketValue)}</span>
               </div>
               {comp.used_startup_costs > 0 && (
                  <div className="flex justify-between text-sm">
                     <span className="text-slate-400 font-bold">Startup Hidden Costs</span>
                     <span className="font-black text-amber-400">+{fmt(comp.used_startup_costs)}</span>
                  </div>
               )}
            </div>
            <div className="mt-8 pt-6 border-t border-slate-800 flex items-start gap-3">
               <Info className="text-slate-500 mt-1" size={16} />
               <p className="text-xs text-slate-400 leading-relaxed italic">
                  * Assumes you sell your current car at its market value ({fmt(analysis.currentMarketValue)}). Actual trade-in values at dealerships may be lower.
               </p>
            </div>
         </div>

         {/* Chart Projection */}
         <div className="space-y-4">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">5 Year Financial Projection</h4>
            <div className="h-[300px] w-full bg-white dark:bg-slate-800 rounded-[2.5rem] p-6 border border-gray-100 dark:border-white/10 shadow-sm relative z-0">
               <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={Array.from({ length: ownershipYears + 1 }, (_, i) => ({
                     yr: i,
                     current: Math.round(currentMonthly * 12 * i),
                     newCar: Math.round((comp.monthly_total) * 12 * i + comp.upfront_cost),
                  }))} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#94a3b8" stopOpacity={0.2}/><stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/></linearGradient>
                      <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/><stop offset="95%" stopColor="#6366f1" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="yr" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis fontSize={9} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v}`} />
                    <Tooltip contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }} />
                    <Area type="monotone" dataKey="current" stroke="#94a3b8" strokeWidth={2} fillOpacity={1} fill="url(#colorCurrent)" name="Current Car" />
                    <Area type="monotone" dataKey="newCar" stroke="#6366f1" strokeWidth={4} fillOpacity={1} fill="url(#colorNew)" name="Switching" />
                  </AreaChart>
               </ResponsiveContainer>
            </div>
         </div>
      </div>
    </div>
  );
}
